<script lang="ts">
  import PlanSelect from '$lib/components/paywall/PlanSelect.svelte';
  import AccountCreation from '$lib/components/paywall/AccountCreation.svelte';
  import Billing from '$lib/components/paywall/Billing.svelte';
  import PromoCode from '$lib/components/paywall/PromoCode.svelte';
  import OfferTimer from '$lib/components/paywall/OfferTimer.svelte';
  import { onMount } from 'svelte';
  import { getCoupon } from '$lib/gateway/stripeClient';
  import { userStore } from '$lib/stores/userStore';
  import { initBraze } from '$lib/gateway/brazeClient';
  import { ensureOptimizely, optimizelyStore } from '$lib/stores/optimizelyStore';
  import { analytics } from '$lib/gateway/segment';
  import {
    isExpired,
    getStoredTimer,
    canStartNewTimer,
    initializeTimer,
    handleCouponAndPromoCode,
  } from '$lib/helpers/timedCouponHelper';
  import type { GetPromoCodeResponse } from '../../api/stripe/promo/[code]/+server.js';
  import type { GetCouponResponse } from '../../api/stripe/coupon/[id]/+server.js';
  import { handleExitIntent } from '$lib/helpers/exitIntentHelpers';

  export let data;
  export let form;

  let price;
  let coupon;
  let country: string | undefined;
  let timedExpiryTime: number | undefined;
  let timedPromoCode: GetPromoCodeResponse | undefined;
  let timedCoupon: GetCouponResponse | undefined;

  const handleTimedCoupon = async () => {
    const { timedCouponId, timedCouponDuration } = data?.pageData ?? {};

    if (!timedCouponId || !timedCouponDuration) {
      return;
    }

    const fetchedCoupon = (await getCoupon(timedCouponId))?.data;

    if (!fetchedCoupon) {
      return;
    }

    timedCoupon = fetchedCoupon;

    const storedEndTime = getStoredTimer(timedCouponId);

    if (storedEndTime) {
      if (isExpired(storedEndTime)) {
        handleExpiredPromoCode();
        return;
      }

      timedExpiryTime = storedEndTime;

      // If user is logged in and we don't have a promo code yet, try to apply the coupon
      if (!$userStore.loading && $userStore.user && !timedPromoCode) {
        timedPromoCode = await handleCouponAndPromoCode();

        if (timedPromoCode) {
          $userStore.promo_code = timedPromoCode.code;
          coupon = timedPromoCode;
        }
      }

      return;
    }

    if (canStartNewTimer(timedCouponId)) {
      const newExpiryTime = initializeTimer(timedCouponDuration, timedCouponId);
      timedExpiryTime = newExpiryTime;

      // If user is logged in, try to apply the coupon immediately
      if (!$userStore.loading && $userStore.user && !timedPromoCode) {
        timedPromoCode = await handleCouponAndPromoCode();

        if (timedPromoCode) {
          $userStore.promo_code = timedPromoCode.code;
          coupon = timedPromoCode;
        }
      }
    }
  };

  const handleAccountCreated = async () => {
    if (timedExpiryTime && timedCoupon && !timedPromoCode) {
      timedPromoCode = await handleCouponAndPromoCode();

      if (timedPromoCode) {
        $userStore.promo_code = timedPromoCode.code;
        coupon = timedPromoCode;
      }
    }
  };

  const handleExpiredPromoCode = async () => {
    timedExpiryTime = 0;
    timedPromoCode = undefined;
    $userStore.promo_code = null;
    coupon = undefined;

    const braze = await initBraze();

    if (braze) {
      braze.logCustomEvent('sweat_web_paywall_offer_expired', {
        slug: data.pageData.slug,
      });

      analytics.track('Paywall Offer Expired');

      window.plausible('new_paywall_offer_expired');
    }
  };

  const handleOptimizely = async () => {
    if (!$optimizelyStore.isReady) {
      await ensureOptimizely();
    }

    if ($optimizelyStore.userContext) {
      $optimizelyStore.userContext.trackEvent('sweat_web_paywall_page_view');
    }
  };

  const handleBraze = async () => {
    const braze = await initBraze();

    handleExitIntent(() => {
      if (braze) {
        braze.logCustomEvent('sweat_web_paywall_exit_intent', {
          slug: data.pageData.slug,
        });
      }

      analytics.track('Paywall Exit Intent');

      window.plausible('new_paywall_exit_intent');
    });
  };

  onMount(() => {
    handleOptimizely();
    handleBraze();
    handleTimedCoupon();
  });
</script>

{#if timedCoupon}
  <!-- Timed Offer -->
  <OfferTimer
    endTime={timedExpiryTime || 0}
    on:expired={() => {
      handleExpiredPromoCode();
    }}
  />
{/if}

<div
  class={`md:flex flex-row items-start ${
    data?.pageData?.timedCouponId && data?.pageData?.timedCouponDuration
      ? 'md:h-[calc(100vh-83px)]'
      : 'md:h-[calc(100vh-179px)]'
  }`}
>
  <div
    class={`relative flex flex-1 h-full min-h-[12.5rem] md:overflow-visible md:block ${
      !data?.pageData.showImageOnMobile ? 'hidden' : ''
    }`}
  >
    <picture>
      <source media="(min-width: 640px)" srcset="{data?.pageData.image?.url}?w=1800&q=85&fm=webp" />
      <source
        srcset="{data?.pageData.mobileImage?.url
          ? data?.pageData.mobileImage?.url
          : data?.pageData.image?.url}?w=1000&q=85&fm=webp"
      />
      <img
        src="{data?.pageData.image?.url}?fm=webp"
        alt={data?.pageData.image?.title}
        class="absolute -z-10 w-full h-full object-cover content-center"
      />
    </picture>

    <div
      class="flex items-center justify-center py-20 px-5 h-full text-center max-w-[488px] mx-auto"
    >
      <div>
        {#if data?.pageData.partnerLogoImage}
          <img
            src="{data?.pageData.partnerLogoImage?.url}?w=90&h=90&fit=fill&fm=webp"
            alt={data?.pageData.partnerLogoImage?.title}
            class="rounded-lg mx-auto mb-6"
          />
        {/if}
        {#if data?.pageData.imageText}
          <h1
            class="text-display-md md:text-display-xl font-bold text-white drop-shadow-[1px_1px_0_rgba(0,0,0,0.4)]"
          >
            {data?.pageData.imageText}
          </h1>
        {/if}
        {#if data?.pageData.imageSubText}
          <p
            class="text-display-2xs md:text-display-md font-medium text-white mt-0 drop-shadow-[1px_1px_0_rgba(0,0,0,0.4)]"
          >
            {data?.pageData.imageSubText}
          </p>
        {/if}
      </div>
    </div>
  </div>

  <div class="h-full flex-1 p-8 overflow-auto" data-testid="join-page">
    <div class="w-full mx-auto max-w-800 flex flex-col gap-10">
      <!-- Choosing a plan -->
      <PlanSelect
        bind:price
        {data}
        {country}
        timedPromoCode={timedPromoCode || (timedCoupon ? { ...timedCoupon, code: '' } : undefined)}
      />

      <!-- Promo code -->
      {#if data?.pageData.allowCouponCode}
        <PromoCode bind:coupon {price} appliedCouponCode={data?.pageData?.appliedCouponCode} />
      {/if}

      <!-- Account login / signup -->
      <AccountCreation {form} {country} on:completed={handleAccountCreated} />

      <!-- Billing -->
      <Billing {data} {price} {coupon} {timedPromoCode} bind:country />
    </div>
  </div>
  {#if timedCoupon && !timedExpiryTime}
    <div class="absolute inset-0 select-none bg-white opacity-40"></div>
  {/if}
</div>
